export const en = {
  status: {
    queued: 'Queued',
    analyzing: 'Analyzing',
    success: 'Success',
    failed: 'Failed',
  },
  table: {
    columns: 'Columns',
    toggle_columns: 'Toggle columns',
    resize_column: 'Resize column',
    resize_column_hint: 'Drag to resize, double-click to reset',
    select_row: 'Select {{name}}',
    select_all_rows: 'Select all visible rows',
    select_all_rows_limited: 'Select up to {{max}} visible rows',
  },
  common: {
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    back: 'Back',
    forward: 'Forward',
    sign_out: 'Sign out',
    no_data: 'No data',
    no_results: 'No results found.',
    n_selected: '{{count}} selected',
    clear_filters: 'Clear filters',
    expand: 'Expand',
    collapse: 'Collapse',
    view_details: 'View details',
    row_actions: 'Actions for {{name}}',
    resize_panel: 'Resize panel',
    resize_panel_hint: 'Drag to resize, double-click to reset',
    page_prev: 'Previous',
    page_next: 'Next',
    page_prev_aria: 'Go to previous page',
    page_next_aria: 'Go to next page',
    copyright: '© {{year}} Lenovo · Internal use only',
  },
  docs: {
    eyebrow: 'Product documentation',
    title: 'Crash Dump Diagnostics',
    subtitle:
      'Everything between a blue screen and a verdict you can file: configuring the rigs, collecting and uploading dumps, reading the AI analysis, and handing the report on.',
    toc: 'On this page',
    updated: 'Based on SOP v1.1 · updated 2026-09-26',
    cta_download: 'Get the clients',
    cta_quickstart: 'Quick start',
    cta_title: 'Ready to get started?',
    cta_body: 'Download BSOD Scout and onboard your first test rig in minutes.',
    cta_button: 'Go to downloads',
    // Labels shared with the release usage blocks on /download.
    code_label_config: 'bsod-relay.json',
    code_label_console: 'Console',
    groups: [
      {
        title: 'Getting started',
        sections: [
          {
            id: 'overview',
            no: '01',
            title: 'What the platform does',
            blocks: [
              {
                p: 'Crash Dump Diagnostics turns a Windows blue screen into evidence: the dump and diagnostic logs are collected and uploaded automatically, the platform queues an AI-assisted analysis, and engineers review the verdict, group repeats into cases, and hand the report to whoever needs it.',
              },
              {
                table: {
                  head: ['Component', 'Form', 'Responsibility'],
                  rows: [
                    [
                      'BSOD Scout',
                      'Single-file Windows EXE (double-click → UAC → GUI)',
                      'Configures crash dumps, collects logs together with the dump, uploads to a project, and can install a startup task. It does not analyze — it gets the evidence to the platform intact.',
                    ],
                    [
                      'bsod-relay',
                      'Single-file Go process (~3.8 MB, no runtime)',
                      'HTTP `CONNECT` forward proxy plus UDP discovery, on a host that reaches both the lab network and the internet. It moves bytes only: no TLS termination, no disk writes, no credential checks.',
                    ],
                    [
                      'Web console',
                      'Browser SPA',
                      'Submits analyses, tracks events and AI verdicts, groups cases, manages devices, members and projects, issues uploader PINs, and hands out read-only share links. Platform admins get a separate Admin console.',
                    ],
                  ],
                },
              },
              {
                p: 'One upload, end to end: Scout registers the event and asks the backend for a presigned URL → the dump and attachments go straight to OSS (multipart for large files) → the backend records the event and queues an analysis → a Worker runs its CDB / WinDbg evidence loop → the verdict, markdown plus structured fields, is written back → engineers read, export and share it in the browser.',
              },
            ],
          },
          {
            id: 'quickstart',
            no: '02',
            title: 'Quick start',
            blocks: [
              {
                p: 'Pick the path that matches your role. They do not conflict: the same person can wire up a rig with the third one and come back to the first.',
              },
              {
                table: {
                  head: ['You are', 'Start here'],
                  rows: [
                    [
                      'An engineer with a dump in hand',
                      'Open `/submit`, pick the project, attach the `.zip`; the platform queues it immediately.',
                    ],
                    [
                      'A test rig that should collect on its own',
                      'Sign in once and install the startup task; every blue screen uploads itself after that.',
                    ],
                    [
                      'Lab IT with no egress from the rig network',
                      'Deploy `bsod-relay` on a bridge host that can reach the internet.',
                    ],
                  ],
                },
              },
              { code: { label: 'Console', text: 'https://bsod.lenovo.com/submit' } },
              {
                code: {
                  label: 'PowerShell',
                  text: 'BsodScout.exe -Login\nBsodScout.exe -InstallTask -ProjectId <PROJECT_ID>',
                },
              },
              {
                code: {
                  label: 'Relay host',
                  text: 'go build -o bsod-relay ./cmd/bsod-relay\n./bsod-relay -check -config bsod-relay.json\n./bsod-relay -check-egress -config bsod-relay.json',
                },
              },
              {
                warn: '"Uploaded" means the event reads Success in the console, not that a zip exists locally. If an event sits in Queued or Analyzing, check Worker and queue health before uploading it again.',
              },
              {
                img: {
                  src: '/sop/img/projects.png',
                  alt: 'Project list',
                  caption:
                    '**Figure 3-3**　Project list: click a row to enter that project. The switcher at the top of the console jumps between projects at any time.',
                },
              },
              {
                img: {
                  src: '/sop/img/project-overview.png',
                  alt: 'Project overview',
                  caption:
                    '**Figure 3-4**　Project overview: the metric tiles jump to a pre-filtered event list; the fixed navigation on the left belongs to the project.',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Web console',
        sections: [
          {
            id: 'access',
            no: '03',
            title: 'Signing in, accounts and roles',
            blocks: [
              {
                p: 'The platform lives at `https://bsod.lenovo.com` and signs you in either through company SSO (ITCode / Keycloak / ADFS) or a local account (email plus password). The first sign-in asks you to read and accept the compliance notice. The UI opens in English; the globe in the top right switches to Chinese, and the choice is remembered per browser.',
              },
              {
                img: {
                  src: '/sop/img/login-compliance.png',
                  alt: 'First visit: the AI policy and privacy notice',
                  caption:
                    '**Figure 3-1**　First visit: the AI-policy and privacy notice, accepted once and not shown again.',
                },
              },
              {
                img: {
                  src: '/sop/img/login.png',
                  alt: 'Sign-in page: SSO and local account',
                  caption:
                    '**Figure 3-2**　Sign-in page: SSO or a local account; the footer links to Docs and Download.',
                },
              },
              {
                table: {
                  head: ['Role', 'Can do'],
                  rows: [
                    ['PA', 'View, upload, comment, and annotate an event description.'],
                    [
                      'Engineer',
                      'Everything a PA can, plus editing cases, external links and comments.',
                    ],
                    [
                      'SA Leader',
                      'Everything an Engineer can, plus managing events (reanalyze, delete), members and project settings.',
                    ],
                    [
                      'Company admin / super admin',
                      'An equivalent role computed per request, never a membership row; super admins also see the Admin console.',
                    ],
                  ],
                },
              },
              {
                p: 'The Account page keeps your profile and password and issues the 12-character uploader PIN (like `A7K2-9QXM-4T3B`) that lets a rig upload without an engineer account. A PIN is not bound to a project or a machine: it can be used repeatedly until it expires, and case, hyphens and look-alike characters (O/0, I/L/1) are interchangeable.',
              },
              {
                img: {
                  src: '/sop/img/account-pin.png',
                  alt: 'Account page: generating an uploader PIN',
                  caption:
                    '**Figure 3-13**　Generate uploader PIN: set the lifetime, generate, hand it to the test rig; revoke it at any time.',
                },
              },
              {
                warn: 'Repeated wrong PINs trip a rate limit — first a back-off per source address, then a site-wide pause on redemption. Never retry-guess a PIN from a script.',
              },
            ],
          },
          {
            id: 'submit',
            no: '04',
            title: 'Submitting a dump by hand',
            blocks: [
              {
                p: 'When there is no rig, or you only want to hand in one more dump, upload from the browser: press **New analysis** in the top right of the events list and drop the `.zip` into the drawer — pick the project, add a description if you want it, then press Start upload.',
              },
              {
                ul: [
                  'Check the project by its `#public ID` first — an upload filed under the wrong project is invisible to the people who need it.',
                  'Only `.zip` is accepted. The browser hashes the file locally (SHA-256) and uploads over TLS, with multipart resume for large files.',
                  'On success the page jumps straight to the new event.',
                ],
              },
              {
                p: 'Besides the dump, put `systeminfo.txt`, `msinfo32.txt`, the driver list and the event logs in the zip — the verdict gets noticeably better. Scout packs these for you; a manual upload has to bring its own.',
              },
              {
                img: {
                  src: '/sop/img/submit.png',
                  alt: 'Submitting a dump by hand',
                  caption:
                    '**Figure 3-5**　The New analysis drawer: the dump first, then the project, then an optional description; the file is hashed locally (SHA-256) before it goes out over TLS.',
                },
              },
            ],
          },
          {
            id: 'events',
            no: '05',
            title: 'Events and the AI verdict',
            blocks: [
              {
                p: 'Each upload becomes one event. The list filters by scope — the project picker in the page band widens it to every project you can see — and by status, and the search box understands field syntax. An event that is still analyzing refreshes itself over SSE — no need to reload the page.',
              },
              {
                img: {
                  src: '/sop/img/events.png',
                  alt: 'Event list',
                  caption:
                    '**Figure 3-6**　Event list: event id, bugcheck, title, upload time and status. Expand a row for its description and faulting module; **Columns** in the top right picks the columns.',
                },
              },
              {
                code: {
                  label: 'Search',
                  text: 'bugcheck:0x9F status:success\nwifi resume\n2606-1',
                },
              },
              {
                table: {
                  head: ['Status', 'What it means'],
                  rows: [
                    [
                      'Queued',
                      'Uploaded and waiting for a Worker. Stuck for a long time? Check Admin → Queue Health.',
                    ],
                    [
                      'Analyzing',
                      'A Worker is running the evidence loop; the page refreshes itself.',
                    ],
                    ['Success', 'A verdict exists — open the report.'],
                    [
                      'Failed',
                      'The analysis stopped (incomplete dump, time limit, …). Open the event and Reanalyze, or read the failure reason.',
                    ],
                  ],
                },
              },
              {
                table: {
                  head: ['Tab', 'Contents'],
                  rows: [
                    ['Summary', 'One-line verdict, suspect modules, top of the stack.'],
                    [
                      'Report',
                      'The full report — root cause, evidence, recommended actions. This is what you send to others.',
                    ],
                    [
                      'Stack / Modules / System',
                      'Call stack, loaded modules, and the machine snapshot (OS, BIOS, CPU, memory, NICs).',
                    ],
                    [
                      'Files / Runs',
                      'The original dump and attachments, plus every analysis attempt for comparison.',
                    ],
                  ],
                },
              },
              {
                p: 'Common actions: **Files** to download the original dump, **Reanalyze** (SA Leader and above) to run it again, **Share** for a read-only link, **Download** in the header to export the report, and **Files / Attach** to add logs gathered later.',
              },
              {
                img: {
                  src: '/sop/img/event-detail.png',
                  alt: 'Event detail: verdict and top of the stack',
                  caption:
                    '**Figure 3-7**　Event detail (Summary tab): the facts row (BUGCHECK / MODULE / COMPUTER) above the AI verdict card — one-line verdict on the left, crash site (top of the stack) on the right.',
                },
              },
              {
                img: {
                  src: '/sop/img/event-report.png',
                  alt: 'Event detail: the Report tab',
                  caption:
                    '**Figure 3-8**　Report tab: the full markdown verdict (root cause, evidence, recommended actions), ready to paste into a defect ticket.',
                },
              },
            ],
          },
          {
            id: 'cases',
            no: '06',
            title: 'Cases, devices and members',
            blocks: [
              {
                p: 'One root cause usually shows up on several machines and several batches. Grouping the related events into a Case makes the unit of work a problem instead of a pile of dumps: a case has a title, a status (open/closed), an event count and a description.',
              },
              {
                img: {
                  src: '/sop/img/cases.png',
                  alt: 'Case list',
                  caption:
                    '**Figure 3-9**　Case list: how many events each case holds and when it was last updated.',
                },
              },
              {
                p: 'Machines that have uploaded a dump are merged into devices by SMBIOS UUID / serial / MAC, so a machine that crashes ten times is still one device. Search by hostname, MAC, serial or model to tell a single machine from a whole batch. SA Leaders and above manage membership and roles under Members.',
              },
              {
                img: {
                  src: '/sop/img/devices.png',
                  alt: 'Device list',
                  caption:
                    '**Figure 3-10**　Device list: hostname, MAC, serial, model, SKU and last report time.',
                },
              },
              {
                img: {
                  src: '/sop/img/members.png',
                  alt: 'Project members',
                  caption:
                    '**Figure 3-11**　Members: PA reads, uploads, comments and annotates an event description, Engineer edits cases, SA Leader manages events, members and project settings.',
                },
              },
              {
                ul: [
                  'One bugcheck plus one suspect module → one case.',
                  'Write the reproduction conditions, the confirmed conclusion and the version expected to fix it into the case description.',
                  'Put the case number (like `#2606-1`) into the defect ticket so it can be traced and verified later.',
                ],
              },
            ],
          },
          {
            id: 'share',
            no: '07',
            title: 'Sharing a read-only report',
            blocks: [
              {
                p: 'To send a verdict to someone outside the platform — a vendor, a sister team — open the event and press Share. The link has an expiry and can be revoked at any time; whoever opens it needs no account and sees only that event’s title, verdict summary and the file download.',
              },
              {
                img: {
                  src: '/sop/img/share-link.png',
                  alt: 'Read-only share page',
                  caption:
                    '**Figure 3-12**　Share page: read-only, switchable between English and Chinese, with the expiry printed at the bottom.',
                },
              },
              {
                warn: 'A share link is anonymous download access to a dump. Send it only to people who need it, keep the expiry short (72 hours, say), and revoke it in the Share panel once the job is done.',
              },
            ],
          },
          {
            id: 'admin',
            no: '08',
            title: 'The Admin console',
            blocks: [
              {
                p: 'Platform super admins get a separate Admin console for users, companies, workers, PI credentials, audit logs, queue health and OIDC providers. System administrators also configure the mailbox that sends analysis-completion mail on the Mail settings page.',
              },
              {
                table: {
                  head: ['Screen', 'Use it to'],
                  rows: [
                    [
                      'Users / Companies',
                      'Accounts, roles (company admin / user), enable and disable.',
                    ],
                    [
                      'Queue Health',
                      'See whether the analysis queue is backing up — the first stop when events sit in Queued.',
                    ],
                    ['Audit Logs', 'Who uploaded, deleted or shared what, and when.'],
                    [
                      'Workers / Relays',
                      'Registration and heartbeat for analysis workers and LAN relays.',
                    ],
                    [
                      'Mail settings',
                      'The Exchange account that notifies project leads when an analysis succeeds, with a test send.',
                    ],
                  ],
                },
              },
              {
                p: 'The completion mail goes to project SA Leaders only — engineers, PAs and company admins are not on the list. It names the event, its bugcheck and suspect module, and links straight to the report.',
              },
              {
                img: {
                  src: '/sop/img/admin-users.png',
                  alt: 'Admin console: user list',
                  caption:
                    '**Figure 3-14**　Admin → Users: role (Company admin / User), status, creation time and the enable/disable action.',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Scout client',
        sections: [
          {
            id: 'scout-install',
            no: '09',
            title: 'Getting, installing and signing in',
            blocks: [
              {
                p: 'Download the single-file EXE from `/download` (`bsod-scout-<version>-windows-x64.exe`, with its SHA-256 shown on the page) and drop it in any directory. There is nothing else to install: no .NET, no PowerShell modules.',
              },
              {
                ul: [
                  'First run: double-click → UAC → main window. The EXE is not code-signed, so SmartScreen may say “Windows protected your PC” — choose **More info → Run anyway**.',
                  'On a domain-joined machine it warns that group policy may override the registry settings it writes. That is expected.',
                  'Collecting and uploading needs administrator rights: it writes `HKLM\\...\\CrashControl` and installs a scheduled task.',
                ],
              },
              {
                p: 'Set the dump type to **Automatic memory dump** or **Complete memory dump** across the fleet — the quality of the verdict depends on how complete the dump is. On domain machines group policy can override it, so make an exception if policy forces mini dumps.',
              },
            ],
          },
          {
            id: 'scout-pipeline',
            no: '10',
            title: 'Collecting and uploading: the pipeline',
            blocks: [
              { p: '**Run scan** walks one pipeline, and the scheduled task runs the same one:' },
              {
                steps: [
                  '**Connectivity** — one real request, direct first; only if that fails does it walk the proxy ladder.',
                  '**Collect logs + compress the dump** — msinfo32, systeminfo, driverquery, the system and application event logs, the built-in GatherLogs package and the newest dump, all into one zip.',
                  '**Upload the dump** — presigned URL, multipart PUT; the log attachments ride along with the event. (Step 4, “Upload logs”, was folded into this one.)',
                  '**Back up to Desktop** — a copy of the zip stays on the machine for offline review or re-upload.',
                ],
              },
              {
                warn: 'Pressing Start locks the upload target and the account controls for the duration of the run: the project and description are only read at step 3, so changing them mid-run would mean the event landed somewhere other than the window said. Closing the window asks first; **Keep running** leaves everything as it is.',
              },
            ],
          },
          {
            id: 'scout-auto',
            no: '11',
            title: 'Unattended rigs and PIN uploads',
            blocks: [
              {
                p: 'Unattended rigs are configured once and then left alone: sign in a single time, then register a startup task bound to the project.',
              },
              {
                code: {
                  label: 'PowerShell',
                  text: 'BsodScout.exe -Login\nBsodScout.exe -InstallTask -ProjectId <PROJECT_UUID>\nBsodScout.exe -RunScan -ProjectId <PROJECT_UUID> -Pin A7K2-9QXM-4T3B',
                },
              },
              {
                p: 'The task is called `BsodScan`, fires 60 seconds after boot, and runs the scan silently as SYSTEM. Its uploads are recognisable in Events as `uploader = Scout (scheduled)`. Run `-UninstallTask` when the machine is retired.',
              },
              {
                table: {
                  head: ['', 'Account (JWT)', 'Uploader PIN'],
                  rows: [
                    [
                      'Where it fits',
                      'The engineer’s own machine',
                      'Shared rigs and line machines with no engineer account',
                    ],
                    [
                      'Rights',
                      'Whatever the account holds in the project',
                      'Read-only project lookup plus upload',
                    ],
                    [
                      'Stored as',
                      '`auth.json` (SYSTEM + Administrators)',
                      'Nothing on disk — exchanged in memory for a short-lived upload token',
                    ],
                    [
                      'Lifetime',
                      'Until the access/refresh token expires',
                      'The PIN’s own expiry; reusable until then',
                    ],
                  ],
                },
              },
              {
                warn: 'Renaming the machine, moving the directory or changing the version leaves the task pointing at the old EXE. Running `-InstallTask` once more repairs it.',
              },
            ],
          },
          {
            id: 'scout-proxy',
            no: '12',
            title: 'Egress: the proxy and relay ladder',
            blocks: [
              {
                p: 'The menu **☰ → Proxy** decides which way scans, uploads and sign-in requests leave the machine: empty means detect automatically, `direct` means always connect straight out, and an address pins that proxy.',
              },
              {
                table: {
                  head: ['Layer', 'Meaning'],
                  rows: [
                    [
                      'L0 configured',
                      'The proxy in the menu, `BSOD_PROXY`, or `proxy.url`. If it matches, nothing else is tried.',
                    ],
                    [
                      'L2c direct probe',
                      'One TCP + TLS reachability check, no HTTP request — a machine that can go direct does not start with the system proxy.',
                    ],
                    ['L2 system proxy', 'WinINET / PAC, including machine-level policy.'],
                    [
                      'L2b candidates',
                      'Plain forward proxies listed in `proxy.discovery.candidates`.',
                    ],
                    ['L3 LAN relay', 'Cache first, then UDP discovery on port 53535.'],
                    [
                      'L4 / L5',
                      'Conventional DNS name, then a plain direct connection as the last resort.',
                    ],
                  ],
                },
              },
              {
                p: 'Every candidate has to pass the probe to win: Scout sends `CONNECT` and then completes a full TLS handshake with the real host against the system trust chain. A proxy that terminates TLS in the middle is rejected, and nobody has to memorise a fingerprint.',
              },
              {
                warn: 'A configured proxy that cannot be reached fails with **Configured proxy unreachable** — it never silently falls back to a direct connection.',
              },
            ],
          },
          {
            id: 'scout-logs',
            no: '13',
            title: 'Logs, state files and troubleshooting',
            blocks: [
              {
                table: {
                  head: ['Path', 'What it is'],
                  rows: [
                    [
                      '`%ProgramData%\\bsod\\auth.json`',
                      'Login tokens and the backend address (credentials inside — do not share).',
                    ],
                    [
                      '`config.json`',
                      'Egress proxy and theme. A `config.json` next to the EXE wins on read and is where the UI writes.',
                    ],
                    [
                      '`project.json`',
                      'The remembered upload target, used when BIOS matching finds nothing.',
                    ],
                    ['`proxy-cache.json`', 'Discovered relays, cached with a 30-minute TTL.'],
                    ['`logs\\BsodScout.log`', 'The run log — the first place to look.'],
                    [
                      '`Desktop\\bsod-backup-<time>.zip`',
                      'The local copy, identical to what was uploaded.',
                    ],
                  ],
                },
              },
              {
                table: {
                  head: ['Symptom', 'Fix'],
                  rows: [
                    [
                      'SmartScreen blocks the EXE',
                      'It is not code-signed: **More info → Run anyway**, or ship it through a trusted channel.',
                    ],
                    [
                      'Step 1 sits in “Retrying in 3s…”',
                      'No direct route and no proxy found: check that the relay is up, allow inbound UDP 53535 and TCP 3128, or enter an address by hand.',
                    ],
                    [
                      'Configured proxy unreachable',
                      'A pinned proxy is down and will not fall back. Fix the address, or switch back to automatic / direct.',
                    ],
                    [
                      '403 after the upload reaches 100%',
                      'The relay allowlist is missing the OSS host — add the hostname from the real signed URL.',
                    ],
                    [
                      'The scheduled task never runs',
                      'The EXE moved or was renamed: run `-InstallTask` again.',
                    ],
                    [
                      'Filed under the wrong project',
                      'The BIOS prefix matched several projects, or an old choice stuck: fix the prefixes and reselect, then verify by `#ID`.',
                    ],
                  ],
                },
              },
            ],
          },
        ],
      },
      {
        title: 'LAN relay',
        sections: [
          {
            id: 'relay',
            no: '14',
            title: 'bsod-relay: deploy, allowlist, verify',
            blocks: [
              {
                p: 'When the rig network has no egress but one host can reach the internet, run `bsod-relay` on that bridge host. It is a plain HTTP `CONNECT` forward proxy: Scout still dials the real hostnames, the relay only moves bytes, and TLS stays end to end. Nothing to sign, no fingerprint to pin, and no dump ever lands on the relay.',
              },
              {
                code: {
                  label: 'Bridge host',
                  text: 'go build -o bsod-relay ./cmd/bsod-relay\ncp config.example.json bsod-relay.json\n./bsod-relay -check -config bsod-relay.json\n./bsod-relay -check-egress -config bsod-relay.json\n./bsod-relay -config bsod-relay.json',
                },
              },
              {
                code: {
                  label: 'bsod-relay.json',
                  text: '{\n  "listen": "0.0.0.0:3128",\n  "beacon": { "enabled": true, "port": 53535 },\n  "allow": {\n    "hosts": ["bsod.lenovo.com", "platform-bsod.oss-cn-hangzhou.aliyuncs.com", "stscn.lenovo.com"],\n    "ports": [443]\n  },\n  "acl": { "allow_cidrs": ["10.0.0.0/8", "192.168.0.0/16"] },\n  "limits": { "max_connections": 64, "idle_timeout": "15m" }\n}',
                },
              },
              {
                p: 'Open inbound TCP 3128 (the proxy) and UDP 53535 (discovery) on the bridge host. Start it from a scheduled task on boot running as SYSTEM, or with NSSM — do not use `sc.exe create`, this program does not implement the service control protocol.',
              },
              {
                warn: 'The egress allowlist has to cover the backend host **and** the real OSS host. Allow only the backend and the probe still passes, while the large PUT fails with 403. Confirm with `-check-egress` instead of guessing.',
              },
              {
                ul: [
                  'Every closed connection writes one JSON line (remote, target, bytes up/down, duration). That is the audit trail — leave it on.',
                  '`403 source address is not allowed` — the client is outside `acl.allow_cidrs`.',
                  '`502 upstream unreachable` — admission and allowlist both passed; the relay host itself cannot get out.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: 'Workflow and reference',
        sections: [
          {
            id: 'workflow',
            no: '15',
            title: 'End to end, and who owns what',
            blocks: [
              {
                steps: [
                  '**Prepare** — install Scout, join the project, issue a PIN, deploy a relay if the network needs one. Owner: engineer / lab IT.',
                  '**Collect** — set the dump type; run the GUI scan or let the scheduled task fire. Owner: engineer.',
                  '**Upload** — the five-step pipeline, presigned straight to OSS. Owner: Scout, automatically.',
                  '**Analyze** — a Worker claims the job and runs CDB / WinDbg. Owner: the platform.',
                  '**Decide and file** — review the verdict, group a case, share a read-only link, close it out. Owner: engineer / lead.',
                ],
              },
              {
                table: {
                  head: ['Role', 'Owns'],
                  rows: [
                    [
                      'Engineer',
                      'Collecting and uploading, reviewing the verdict, filing and sharing.',
                    ],
                    [
                      'SA Leader',
                      'Membership and settings, the success/failure call, closing cases.',
                    ],
                    ['Lab IT', 'Relay deployment and the egress allowlist.'],
                    ['Platform admin', 'Workers, queue health, accounts, mail notifications.'],
                  ],
                },
              },
            ],
          },
          {
            id: 'glossary',
            no: '16',
            title: 'Terms and quick reference',
            blocks: [
              {
                table: {
                  head: ['Term', 'English', 'Meaning'],
                  rows: [
                    [
                      '事件',
                      'Event',
                      'One upload: the dump, its attachments and every analysis run against it.',
                    ],
                    [
                      '分析运行',
                      'Analysis run',
                      'The Nth analysis of one event — automatic the first time, manual afterwards.',
                    ],
                    ['案例', 'Case', 'Several same-origin events tracked as one problem.'],
                    ['设备', 'Device', 'A machine asset merged by SMBIOS UUID / serial / MAC.'],
                    ['蓝屏错误码', 'Bugcheck', 'e.g. `0x9F` = DRIVER_POWER_STATE_FAILURE.'],
                    [
                      '上传 PIN',
                      'Uploader PIN',
                      'A 12-character credential for rigs that upload without signing in.',
                    ],
                    [
                      '中继',
                      'Relay',
                      'The HTTP CONNECT forward proxy on a host that can reach the internet.',
                    ],
                    [
                      '预签名 URL',
                      'Presigned URL',
                      'A time-limited upload address signed by the backend.',
                    ],
                  ],
                },
              },
              {
                table: {
                  head: ['Port / path', 'Use'],
                  rows: [
                    ['443/tcp', 'Platform and OSS upload over HTTPS — must be open.'],
                    ['3128/tcp', 'Default relay listener.'],
                    ['53535/udp', 'Relay discovery beacon.'],
                    [
                      '`bsod.lenovo.com`',
                      'The console; `/docs` is this page, `/download` has the clients.',
                    ],
                  ],
                },
              },
              {
                p: 'This page is updated with the product. If the interface and the description disagree, the running platform wins — tell the platform admins so the difference makes it into the next revision.',
              },
            ],
          },
        ],
      },
    ],
  },
  download: {
    title: 'Download',
    subtitle: 'Client binaries for Crash Dump Diagnostics. Windows x64 only.',
    empty: 'No releases published yet. Check back later.',
    load_error: 'Failed to load releases.',
    retry: 'Retry',
    version: 'Version',
    platform: 'Platform',
    format: 'Format',
    size: 'Size',
    published: 'Released',
    sha256: 'SHA-256',
    copied: 'Copied',
    copy: 'Copy',
    download: 'Download',
    kind_scout: 'Windows GUI client for test rigs',
    kind_relay: 'LAN forward proxy for rigs with no internet access',
    view_docs: 'New here? Read the platform overview and quick start',
    usage_title: 'Usage',
    usage_scout:
      'Windows GUI for test rigs: sign in, collect and upload with one click, or install the startup task:',
    usage_scout_code: 'BsodScout.exe -Login\nBsodScout.exe -InstallTask -ProjectId <PROJECT_ID>',
    usage_scout_relay_hint:
      'Rigs without internet access reach the backend through a relay on a bridge host (bsod-relay.json):',
    usage_scout_relay_code:
      '{\n  "listen": "0.0.0.0:3128",\n  "beacon": { "enabled": true, "port": 53535 },\n  "allow": {\n    "hosts": ["bsod.lenovo.com", "platform-bsod.oss-cn-hangzhou.aliyuncs.com"],\n    "ports": [443]\n  }\n}',
    usage_relay:
      'Run it on a bridge host that can reach the platform, then point the rigs at it — the relay needs no account or credential of its own:',
    usage_relay_code:
      'bsod-relay.exe -check -config bsod-relay.json\nbsod-relay.exe -config bsod-relay.json',
  },
  // /settings — the console's own appearance. Names the *effect*, not the
  // colour: a reader picking "Blue" is picking what the links and buttons will
  // do, and the swatch beside it already shows the colour itself (spec §32).
  settings: {
    subtitle: 'How the console looks and reads.',
    theme: 'Theme',
    theme_hint: 'Follow the system, or pin one.',
    accent: 'Accent colour',
    accent_hint: 'Links, buttons and the current page.',
    accent_note:
      'Saved in this browser. Status colours — success, in progress, failed — do not change with it.',
    accent_red: 'Red',
    accent_orange: 'Orange',
    accent_amber: 'Amber',
    accent_green: 'Green',
    accent_teal: 'Teal',
    accent_blue: 'Blue',
    accent_violet: 'Violet',
    accent_ink: 'Monochrome',
    language: 'Language',
    language_hint: 'Interface text only.',
  },
  public: {
    nav_docs: 'Docs',
    nav_download: 'Download',
    sign_in: 'Sign in',
    enter_console: 'Console',
    footer: 'Crash dump collection and analysis',
    // The footer links to a generated plain-text licence notice.
    license_toggle: 'Open source',
  },
  welcome: {
    eyebrow: 'Lenovo internal platform',
    title_lead: 'From a crash dump',
    title_tail: 'to a clear answer',
    lede: 'Collect from the fleet, symbolise automatically, and get the driver, module and likely trigger in one verdict.',
    cta_start: 'Sign in to get started',
    diagram_trace_label: 'Crash trace',
    diagram_suspect: 'Suspect',
    diagram_capture_label: 'Collection path from rigs to a project event',
    diagram_bench: 'Test bench',
    diagram_fleet: 'Lab fleet',
    diagram_bundle: 'Dump + logs',
    diagram_event: 'Project event',
    how_title: 'One evidence trail, from upload to verdict',
    how_lede:
      'The dump, the parsed stack and the reasoning stay beside the answer, so the next reviewer does not start over.',
    closing_line:
      'Every verdict stays tied to the dump, the stack and the module that produced it.',
    example_label: 'Example',
    example_title: 'Crash on resume',
    example_cause_label: 'Root cause',
    example_cause: 'The driver failed to power down.',
    step_collect_title: 'Collect',
    step_collect_body:
      'One click on a test bench, or an unattended startup task on every machine in the lab.',
    step_analyse_title: 'Analyse',
    step_analyse_body:
      'Every dump is symbolised and screened for the faulting stack, the driver version and the patterns that keep coming back.',
    step_verdict_title: 'Verdict',
    step_verdict_body:
      'You get the bugcheck code, the suspect module and a short written verdict, with the evidence behind it.',
  },
  auth: {
    heading: 'Sign in',
    lede: "Use your work account, or continue with your company's single sign-on.",
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    failed: 'Sign-in failed. Check your email and password.',
    or_email: 'or with email',
    email_option: 'Use email instead',
    email_hide: 'Hide email sign-in',
    sso_with: 'Continue with {{name}}',
    help: 'Trouble signing in? Contact your administrator.',
    captcha_required: 'Please complete the verification',
    captcha_failed: 'Captcha verification failed — please try again.',
  },
  nav: {
    open_menu: 'Open menu',
    home: 'Home',
    // The rail's project row: the name is the row's text, so the accessible
    // name has to say what the row *is* (a control), not just repeat it.
    pick_project_hint: 'Pick a project to see its navigation.',
    switch_project: 'Switch project',
    select_project: 'Select a project',
    search_projects: 'Search projects',
    no_projects_match: 'No projects match',
    // The rail's count chip on the Events row: uploads in the last 24h across
    // every project the reader can see (used as a tooltip + accessible name).
    recent_uploads: '{{count}} uploaded in the last 24 hours, across your projects',
    dashboard: 'Dashboard',
    common_defects: 'Common',
    events: 'Events',
    cases: 'Cases',
    projects: 'Projects',
    all_projects: 'All projects',
    docs: 'Docs',
    download: 'Download',
    settings: 'Settings',
    submit: 'New analysis',
    devices: 'Devices',
    catalog: 'Catalog',
    admin: 'Admin',
    account: 'Account',
    language: 'Language',
    theme: 'Theme',
    theme_light: 'Light',
    theme_dark: 'Dark',
    theme_system: 'System',
    back_to_console: 'Back to Crash Dump Diagnostics',
    role_pa: 'PA',
    role_engineer: 'Engineer',
    role_lead: 'SA Leader',
    role_admin: 'Admin',
    role_super_admin: 'Super admin',
  },
  command: {
    trigger: 'Search',
    hint: 'Jump to a page or a project, or search events.',
    placeholder: 'Search events, pages and projects…',
    empty: 'No results.',
    search_group: 'Search',
    search_events: 'Search events for "{{query}}"',
    navigation: 'Navigation',
    actions: 'Actions',
    projects: 'Projects',
    theme: 'Theme',
    links: 'Links',
  },
  errors: {
    auth_token_invalid: 'Your session has expired. Please sign in again.',
    rbac_forbidden: "You don't have permission to do that.",
    not_found: 'Not found.',
    network: 'Network error. Check your connection.',
    unknown: 'Unexpected error.',
  },
  pages: {
    dashboard_title: 'Dashboard',
    common_defects_title: 'Common',
    events_title: 'Events',
    cases_title: 'Cases',
    projects_title: 'Projects',
    submit_title: 'New analysis',
    account_title: 'Account',
    settings_title: 'Settings',
    not_found_title: 'Page not found',
    not_found_body: 'The page you are looking for does not exist.',
    coming_soon: 'Coming soon',
  },
  projects: {
    list: {
      new_project: 'New project',
      no_projects_title: 'No projects yet',
      no_projects_can_create: 'Create one to get started.',
      no_projects_no_perm: 'Ask an admin to add you to a project.',
      col_bios_prefixes: 'BIOS prefixes',
      col_updated: 'Updated',
      row_actions: 'Actions for {{name}}',
      row_edit: 'Edit',
      row_members: 'Members',
      modal_title: 'New project',
      name_label: 'Name',
      name_placeholder: 'Workstation 7000',
      description_label: 'Description',
      description_placeholder: 'Optional',
      owner_company_label: 'Owner company',
      owner_company_placeholder: 'Select company',
      lead_label: 'SA Leader',
      lead_placeholder: 'Pick an SA Leader',
      lead_search: 'Search by name or email',
      lead_empty: 'No users match',
      lead_required: 'Pick an SA Leader',
      members_label: 'Members',
      members_hint: 'Optional. Each person joins the project with the role you pick.',
      member_add: 'Add member',
      member_placeholder: 'Pick a member',
      member_remove: 'Remove member',
      member_required: 'Pick a user for every member',
      tab_active: 'Active',
      tab_archived: 'Archived',
      search_placeholder: 'Search projects…',
      subtitle: '{{count}} projects',
      subtitle_with_company: '{{count}} projects across {{company}}',
    },
    overview: {
      metric_recent: 'Recent events',
      recent_events: 'Recent events',
      recent_cases: 'Recent cases',
      no_events: 'No events yet',
      no_cases: 'No cases yet',
      no_cases_desc: 'Create a case to track issues.',
      facts: 'Project facts',
      fact_devices: 'Devices',
      fact_members: 'Members',
      fact_public_id: 'Public ID',
      fact_created: 'Created',
    },
    members: {
      title: 'Members',
      subtitle:
        'PA can read, upload dumps, comment and edit an event description; engineers add case editing; leads manage events, members and settings.',
      add: 'Add member',
      modal_title: 'Add member',
      col_name: 'Name',
      col_email: 'Email',
      col_role: 'Role',
      col_joined: 'Joined',
      role_pa: 'PA',
      role_engineer: 'Engineer',
      role_lead: 'SA Leader',
      confirm_change_role: 'Change role to {{role}}?',
      confirm_remove: 'Remove {{name}}?',
      empty: 'No members yet',
      email_placeholder: 'engineer@bsod.test',
      search_placeholder: 'Search by name or email',
      no_match: 'No members match',
    },
    member_groups: {
      title: 'User groups',
      subtitle: 'Create reusable sets of people, then add a whole group to a project in one step.',
      page_subtitle: 'Reusable member sets for this company. Apply one to any project you manage.',
      manage: 'Manage groups',
      new_group: 'New group',
      new_title: 'New user group',
      edit_title: 'Edit user group',
      editor_hint: 'Groups are reusable shortcuts. Editing one only changes future additions.',
      name: 'Group name',
      name_placeholder: 'e.g. Platform team',
      role: 'Project role',
      role_for: 'Role for {{name}}',
      selected: 'Selected members',
      add_member: 'Add member',
      search_members: 'Search company users',
      no_members_selected: 'No members selected',
      no_candidates: 'No matching people',
      no_members_yet: 'No active members',
      no_groups: 'No user groups yet',
      no_groups_desc: 'Create a group once, then reuse it in any project in this company.',
      none_for_add: 'No user groups are available for this company yet.',
      load_failed: 'Could not load user groups',
      total: '{{count}} groups',
      total_one: '{{count}} group',
      total_other: '{{count}} groups',
      member_count_one: '{{count}} member',
      member_count_other: '{{count}} members',
      name_required: 'Enter a group name.',
      members_required: 'Add at least one member.',
      save: 'Save group',
      created: 'Group created',
      updated: 'Group updated',
      deleted: 'Group deleted',
      save_failed: 'Could not save the group',
      apply_title: 'Apply group to projects',
      apply_desc:
        'Choose projects that should receive the current members. Existing members keep their roles.',
      apply_selected: '{{count}} selected',
      apply_select_all: 'Select all',
      apply_no_projects: 'No projects you can manage',
      apply_skip: 'Not now',
      apply_action: 'Apply to {{count}} projects',
      apply_action_one: 'Apply to 1 project',
      apply_to_projects: 'Apply to projects',
      company_scope_hint: 'Shared across every project in this company',
      apply_choose: 'Select at least one project.',
      apply_success: 'Updated {{projects}} projects · added {{added}} · skipped {{skipped}}',
      apply_partial: 'Updated {{projects}} projects · {{failed}} failed',
      apply_failed: 'Could not apply to every project',
      edit: 'Edit {{name}}',
      delete: 'Delete {{name}}',
      remove_member: 'Remove {{name}}',
      delete_title: 'Delete {{name}}?',
      delete_desc: 'Projects that already added these people keep their current members.',
      person_tab: 'One person',
      group_tab: 'From user group',
      group: 'User group',
      choose_group: 'Choose a group',
      choose_group_error: 'Choose a user group.',
      add_hint:
        'Adds each member with their role in the group. People already in the project are skipped.',
      add_group: 'Add group',
      added_to_project: 'Added {{added}} · skipped {{skipped}}',
      add_failed: 'Could not add the user group.',
    },
    settings: {
      title: 'Project settings',
      subtitle: 'Name, BIOS prefixes and lifecycle for this project.',
      public_id_prefix: 'Public ID',
      identity: 'Identity',
      name_label: 'Name',
      description_label: 'Description',
      save: 'Save',
      save_changes: 'Save changes',
      saved: 'Saved',
      lifecycle: 'Lifecycle',
      archived_msg:
        'This project is archived. It is hidden from default lists but data remains accessible.',
      active_msg: 'This project is active and receiving dumps.',
      archive: 'Archive',
      unarchive: 'Unarchive',
      confirm_archive: 'Archive project?',
      danger_zone: 'Danger zone',
      danger_msg: 'Deletion is permanent. All events, cases, devices, members are removed.',
      delete: 'Delete project',
      confirm_delete_title: 'Delete project {{name}}?',
      confirm_delete_desc: 'This cannot be undone.',
      bios_prefixes_label: 'BIOS prefixes',
      bios_prefixes_hint:
        'Scout auto-selects this project when the machine BIOS version starts with one of these prefixes.',
      bios_prefixes_save_failed: 'Failed to save BIOS prefixes',
    },
    devices: {
      title: 'Devices',
      subtitle: 'Devices that have uploaded to this project.',
      col_hostname: 'Hostname',
      col_mac: 'MAC',
      col_serial: 'Serial',
      col_model: 'Model',
      col_sku: 'SKU',
      col_last_seen: 'Last seen',
      unknown_model: 'Unknown Model',
      unknown_sku: 'Unknown SKU',
      never: 'never',
      empty: 'No devices yet',
      search_placeholder: 'Search by hostname, MAC, serial, model or SKU',
      no_match: 'No devices match',
    },
    catalog: {
      title: 'Catalog',
      subtitle: 'Models group SKUs; each device is bound to one SKU.',
      new_model: 'New model',
      edit_model: 'Edit model',
      confirm_delete_model: 'Delete model and all SKUs?',
      new_sku: 'New SKU',
      edit_sku: 'Edit SKU',
      confirm_delete_sku: 'Delete SKU {{code}}?',
      edit: 'Edit',
      col_updated: 'Updated',
      models_count: 'Models',
      models_count_with: 'Models ({{count}})',
      skus_for: 'SKUs for {{name}}',
      select_model_prompt: 'Select a model',
      pick_model_left: 'Pick a model on the left',
      no_models: 'No models',
      no_skus: 'No SKUs yet',
      back_to_models: 'Back to models',
      name_label: 'Name',
      description_label: 'Description',
      code_label: 'Code',
      model_name_placeholder: 'ThinkPad X1 Carbon',
      sku_code_placeholder: '20U9-CTO1WW',
      description_placeholder: 'Optional',
    },
  },
  submit: {
    description_label: 'Description',
    description_placeholder: 'Describe what happened (optional)',
    project_label: 'Project',
    project_placeholder: 'Pick a project',
    file_label: 'Dump file (.zip)',
    file_hint: 'Drop a .zip dump here',
    file_hash_hint: 'Hashed locally before upload.',
    choose_file: 'Choose file',
    start: 'Start upload',
    hashing: 'Hashing locally…',
    uploading: 'Uploading to OSS…',
    finalizing: 'Registering event…',
    done: 'Done. Redirecting…',
    failed_msg: 'Failed.',
    subtitle:
      'Upload a Windows crash dump (.zip) to decode the bugcheck and generate an AI root-cause.',
    file_zip_only: 'Only .zip uploads are supported. Large files use resumable multipart upload.',
    zip_only_error: 'That is not a .zip file. Upload the dump as a .zip.',
    tls_note: 'Hashed & uploaded over TLS',
    desc_optional: '· optional',
    replace: 'Replace',
    remove: 'Remove',
    sha_verified: '· SHA-256 verified locally',
    project_hint: 'The dump becomes an event in this project.',
    description_hint: 'What happened before the crash — it helps the report.',
    need_project: 'Pick a project to start',
    need_file: 'Choose a .zip dump to start',
  },
  account: {
    profile: 'Profile',
    password: 'Change password',
    memberships: 'Project memberships',
    email: 'Email',
    company: 'Company',
    roles: 'Roles',
    display_name: 'Display name',
    save: 'Save',
    updated: 'Updated',
    password_changed: 'Password changed',
    profile_saved: 'Profile updated',
    change_avatar: 'Change avatar',
    add_avatar: 'Add avatar',
    remove_avatar: 'Remove avatar',
    avatar_updated: 'Avatar updated',
    avatar_removed: 'Avatar removed',
    avatar_update_failed: 'Could not update avatar',
    avatar_remove_failed: 'Could not remove avatar',
    avatar_type_error: 'Choose a PNG, JPEG, or WebP image.',
    avatar_source_size_error: 'Choose an image smaller than 10 MB.',
    avatar_output_size_error: 'That image is too large after resizing.',
    avatar_process_error: 'Could not process that image.',
    avatar_crop_title: 'Crop avatar',
    avatar_crop_description: 'Drag the image to reposition it, then adjust the zoom.',
    avatar_crop_preview: 'Avatar crop preview',
    avatar_crop_zoom: 'Zoom',
    avatar_crop_reset: 'Reset',
    avatar_crop_apply: 'Use photo',
    no_projects: 'You are not a member of any project yet.',
    events_arrow: 'events →',
    current_password: 'Current password',
    new_password: 'New password',
    confirm_password: 'Confirm new password',
    password_min_12: 'At least 12 characters',
    passwords_mismatch: 'Passwords do not match',
    role_super_admin: 'super-admin',
    role_company_admin: 'company admin',
    role_member: 'member',
    read_only: 'Read-only',
    password_note: "Minimum 12 characters. You'll stay signed in on this device.",
    strength_weak: 'Weak',
    strength_fair: 'Fair',
    strength_good: 'Good',
    strength_strong: 'Strong',
  },
  share: {
    invalid_or_expired: 'This link is invalid or expired',
    untitled: 'Untitled analysis',
    status_label: 'Status',
    uploaded_label: 'Uploaded',
    files_heading: 'Files',
    download: 'Download',
    expires_at: 'This link expires at',
    toggle_zh: '中文',
    toggle_en: 'EN',
  },
  device: {
    identity: 'Identity',
    telemetry: 'Telemetry',
    catalog_assignment: 'Catalog assignment',
    edit: 'Edit',
    save_assignment: 'Save assignment',
    clear: 'Clear',
    model_label: 'Model',
    sku_label: 'SKU',
    select_model: 'Select a model',
    select_sku: 'Select a SKU',
    pick_model_first: 'Pick a model first',
    hostname_label: 'Hostname',
    serial_label: 'Serial',
    smbios_uuid_label: 'SMBIOS UUID',
    primary_mac_label: 'Primary MAC',
    all_macs_label: 'All MACs',
    device_id_label: 'Device id',
    created_label: 'Created',
    updated_label: 'Updated',
    last_seen_label: 'Last seen',
    last_event_label: 'Last event',
    never: 'never',
    edit_title: 'Edit device',
    edit_hostname: 'Hostname',
    edit_mac: 'MAC address',
    edit_serial: 'Serial number',
    catalog_saved: 'Catalog assignment saved',
    catalog_cleared: 'Catalog assignment cleared',
    last_seen_at: 'Last seen {{time}}',
    never_seen: 'Never seen',
    pick_both_or_clear: 'Pick both model and SKU, or use Clear to remove the assignment.',
    unknown_model: 'Unknown Model',
    unknown_sku: 'Unknown SKU',
  },
  pending: {
    title: 'Account pending approval',
    subtitle_with_email: "Your sign-in with {{email}} hasn't been activated yet.",
    subtitle: 'Your sign-in request is awaiting administrator approval.',
    sign_in_again: 'Sign in again',
    body: "An administrator has been notified. Once your account is approved you'll be told via email or your usual internal channel.",
  },
  dashboard: {
    greeting_morning: 'Good morning',
    greeting_afternoon: 'Good afternoon',
    greeting_evening: 'Good evening',
    metric_recent_events: 'Recent events',
    metric_analyzing_now: 'Analyzing now',
    metric_success_24h: 'Success · 24h',
    metric_failed_24h: 'Failed · 24h',
    events_per_day: 'Events per day',
    top_bugchecks: 'Top bugchecks',
    no_project_title: 'No project selected',
    no_project_desc: 'Pick a project from the switcher at the top right.',
    recent_activity: 'Recent activity',
    view_all: 'View all →',
    live: 'Live',
    range_7d: '7d',
    range_30d: '30d',
    range_90d: '90d',
    range_all: 'All',
    no_events_title: 'No events yet',
    no_events_desc: 'Submit a dump to get started.',
    dump_singular: 'dump',
    dumps_plural: 'dumps',
    landed_last_hour: 'landed in the last hour',
    delta_new: 'new',
  },
  /**
   * The cross-project landing page (spec §27). Tile and panel titles it shares
   * with the project dashboard live in `dashboard` above, so the two pages
   * cannot drift apart in wording.
   */
  overview: {
    subtitle: 'Everything landing in your {{count}} projects',
    metric_events: 'Events · {{days}}d',
    recent_events: 'Latest events',
    recent_cases: 'Recently updated cases',
  },
  common_defects: {
    feature_name: 'Common Defects',
    fact_qualifier: 'Common Defect Detection',
    subtitle: 'Common Defects · Signatures recurring across multiple projects: {{count}}',
    empty_title: 'No common defects found',
    empty_desc:
      'A defect appears here when the same bucket signature occurs in at least two projects.',
    col_signature: 'Signature',
    col_module: 'Faulting module',
    col_projects: 'Projects',
    col_events: 'Events',
    col_cases: 'Cases',
    col_last_seen: 'Last seen',
    col_project: 'Project',
    col_event: 'Event',
    col_title: 'Title',
    col_case: 'Case',
    col_status: 'Status',
    col_uploaded: 'Uploaded',
    fact_module: 'Faulting module',
    fact_first_seen: 'First seen',
    fact_last_seen: 'Last seen',
    metric_projects: 'Projects',
    metric_events: 'Events',
    metric_cases: 'Cases',
    metric_accessible: 'Visible',
    affected_projects: 'Affected projects',
    visible_subset: 'Showing projects you can access',
    no_access_title: 'No project evidence available',
    no_access_desc:
      'You can see the aggregate, but none of the projects containing this defect are visible to you.',
    recent_events: 'Recent events',
    events_hidden: 'Showing {{shown}} of {{total}}',
    events_limited: 'Latest {{count}} shown',
    no_events_title: 'No accessible events',
    no_events_desc: 'Events from projects you cannot access are not shown.',
    panel_description: 'Common Defects · Cross-project evidence for this crash signature',
    not_detected_title: 'No common defect detected',
    not_detected_desc:
      'This signature has not recurred across multiple projects in the selected window.',
  },
  events: {
    recent_upload_hint: 'Uploaded in the last 24 hours',
    new_badge: 'NEW',
    common_defect_badge: 'Common',
    common_defect_badge_hint: 'Recurs across {{count}} projects',
    row_summary: 'Crash summary',
    row_facts: 'Event facts',
    row_common_defects: 'Common',
    common_defect_loading: 'Checking common defects…',
    common_defect_summary: 'Projects {{projects}} · Events {{events}} · Cases {{cases}}',
    common_defect_visible: '{{visible}} of {{total}} projects visible',
    common_defect_not_detected: 'No cross-project recurrence found',
    common_defect_failed: 'Could not check common defects',
    compare_selected: '{{count}} selected',
    compare_hint: 'Select at least 2 events to compare.',
    compare_launch: 'Compare selected',
    compare_title: 'Compare events',
    compare_description: '{{count}} events · key analysis and machine facts',
    compare_field: 'Field',
    compare_only_diff: 'Only differences',
    compare_no_fields: 'No comparable fields for these events.',
    compare_no_differences: 'No differences in these fields.',
    compare_differs: 'Differs',
    compare_probably_caused_by: 'Probably caused by',
    compare_crash_frame: 'Top stack frame',
    compare_bugcheck_parameters: 'Bugcheck parameters',
    compare_os: 'OS',
    compare_machine: 'Machine',
    compare_memory: 'Memory',
    compare_memory_value: '{{total}} total · {{available}} available',
    compare_memory_available: '{{available}} available',
    compare_cpu: 'CPU',
    compare_threads: '{{count}} threads',
    select_project_title: 'Select a project',
    select_project_hint: 'Choose a project from the switcher at the top right to view its events.',
    search_placeholder: 'Search or key:value (e.g. bugcheck:0xa0 status:success)',
    col_project: 'Project',
    col_status: 'Status',
    col_event_id: 'Event ID',
    col_bugcheck: 'Bugcheck',
    col_title: 'Title',
    col_uploaded: 'Uploaded',
    col_description: 'Description',
    col_faulting_module: 'Faulting module',
    col_bucket_id: 'Bucket id',
    col_bios: 'BIOS',
    col_uploader: 'Uploader',
    col_case: 'Case',
    open_case: 'Open case',
    case_untitled: 'Untitled case',
    related_events_failed: 'Could not load linked events',
    col_tags: 'Tags',
    no_match_title: 'No events match this search',
    no_match_filters: 'No events with these statuses',
    no_events_title: 'No events yet',
    no_events_desc: 'Submit a dump to get started.',
    description_add: 'Add description',
    description_edit: 'Edit description',
    description_placeholder: 'Add a description…',
    description_save_failed: 'Could not save the description',
  },
  cases: {
    select_project_title: 'Select a project',
    select_project_hint: 'Choose a project from the switcher at the top right to view its cases.',
    all_statuses: 'All',
    status_open: 'Open',
    status_closed: 'Closed',
    search_placeholder: 'Search or key:value (e.g. status:open creator:worker)',
    col_project: 'Project',
    col_status: 'Status',
    col_case_id: 'Case ID',
    col_title: 'Title',
    col_events: 'Events',
    col_created: 'Created',
    col_updated: 'Updated',
    no_match_title: 'No cases match this search',
    no_cases_title: 'No cases',
    no_cases_desc: 'Cases are auto-created by the worker.',
  },
  case_detail: {
    back_to_cases: 'Back to cases',
    tab_description: 'Description',
    tab_comments: 'Comments',
    tab_links: 'External links',
    edit: 'Edit',
    close_case: 'Close case',
    reopen_case: 'Reopen case',
    confirm_close: 'Close case?',
    confirm_reopen: 'Reopen case?',
    rail_title: 'Case',
    fact_status: 'Status',
    fact_id: 'ID',
    fact_created: 'Created',
    fact_events: 'Events',
    fact_signature: 'Signature',
    linked_events: 'Linked events',
    no_events_linked: 'No events linked',
    no_description: 'No description',
    no_comments: 'No comments yet',
    no_links: 'No links',
    post_comment: 'Post',
    comment_placeholder: 'Write a markdown comment…',
    comment_hint: 'Markdown supported · Ctrl/⌘+Enter to send',
    link_url_placeholder: 'https://…',
    link_title_placeholder: 'Title (optional)',
    add_link: 'Add',
    delete_comment: 'Delete comment?',
    delete_link: 'Delete link?',
    edit_case: 'Edit case',
    case_title_label: 'Title',
    description_label: 'Description (markdown)',
  },
  admin: {
    admin_mode: 'Admin mode',
    admin_section: 'Admin',
    // Users
    users_title: 'Users',
    users_subtitle: 'Manage user accounts, roles, and access.',
    member_groups_title: 'User groups',
    users_tab_active: 'Active',
    users_tab_pending: 'Pending',
    users_filter_company: 'Filter by company',
    users_new: 'New user',
    users_col_email: 'Email',
    users_col_name: 'Name',
    users_col_role: 'Role',
    users_col_status: 'Status',
    users_col_created: 'Created',
    users_role_super_admin: 'Super admin',
    users_role_company_admin: 'Company admin',
    users_role_user: 'User',
    users_status_active: 'Active',
    users_status_disabled: 'Disabled',
    users_empty: 'No users',
    pending_approve: 'Approve',
    pending_reject: 'Reject',
    pending_col_email: 'Email',
    pending_col_domain: 'Domain',
    pending_col_provider: 'Provider',
    pending_col_requested: 'First requested',
    pending_col_retries: 'Retries',
    // Companies
    companies_title: 'Companies',
    companies_subtitle: 'Tenant boundaries. Each user, project, and event belongs to a company.',
    companies_new: 'New company',
    companies_col_name: 'Name',
    companies_col_slug: 'Slug',
    companies_col_domains: 'Email domains',
    companies_col_created: 'Created',
    companies_empty: 'No companies',
    // Workers
    workers_title: 'Workers',
    workers_subtitle: 'Analysis workers that consume jobs from the queue.',
    workers_add: 'Add worker',
    workers_add_title: 'Add a worker',
    workers_add_desc:
      'Creates the worker and its key in one step, and prepares the command for the host.',
    workers_add_submit: 'Create worker',
    worker_rename: 'Rename',
    worker_rename_title: 'Rename {{name}}',
    worker_rename_desc:
      'Only the label changes — the key the host holds stays valid, and its state is untouched.',
    worker_rename_submit: 'Save name',
    workers_renamed: 'Worker renamed',
    workers_rename_failed: 'Failed to rename the worker',
    workers_created: 'Worker created',
    workers_create_failed: 'Failed to create the worker',
    worker_name_label: 'Worker name',
    worker_region_label: 'Region',
    worker_id_label: 'Worker ID',
    worker_created_title: 'Worker {{name}} created',
    worker_created_desc:
      'Run this on the worker host — it stores the key and nothing else is needed. The key is shown only once.',
    worker_command_label: 'PowerShell on the worker host',
    worker_key_label: 'Worker key',
    worker_copy_command: 'Copy command',
    worker_copy_key: 'Copy key',
    worker_copied: 'Copied to clipboard',
    worker_created_hint:
      'It appears in this list as offline until it checks in — then start the loop with `bsod-worker run`.',
    workers_active_only: 'Active only',
    workers_col_name: 'Name',
    workers_col_region: 'Region',
    workers_col_version: 'Version',
    workers_col_last_seen: 'Last seen',
    workers_empty: 'No workers',
    workers_col_runtime: 'Status',
    workers_col_ip: 'Public IP',
    workers_col_current_event: 'Current event',
    workers_col_health: 'Health',
    worker_rt_busy: 'Running',
    worker_rt_idle: 'Idle',
    worker_rt_offline: 'Exited',
    worker_rt_revoked: 'Revoked',
    worker_rt_paused: 'Paused',
    worker_health_ok: 'OK',
    worker_health_degraded: 'Degraded',
    worker_health_model_down: 'Model down',
    worker_pause: 'Pause claim',
    worker_resume: 'Resume claim',
    worker_revoke: 'Revoke',
    worker_pause_confirm: 'Pause {{name}}? Any in-flight task is aborted and requeued.',
    // Relays
    relays_title: 'Relays',
    relays_subtitle: 'LAN egress relays, as reported by their own heartbeats.',
    relays_online: 'Online',
    relays_active_connections: 'Active connections',
    relays_egress_down: 'Egress degraded',
    relays_transferred: 'Transferred',
    relays_dial_failures: 'Upstream failures',
    relays_col_name: 'Relay',
    relays_col_owner: 'Owner',
    relays_col_status: 'Status',
    relays_col_egress: 'Egress',
    relays_col_listen: 'Listen',
    relays_col_version: 'Version',
    relays_col_active: 'Active',
    relays_col_traffic: 'Transferred',
    relays_col_errors: 'Errors',
    relays_col_last_seen: 'Last seen',
    relays_no_errors: 'None since start',
    relays_add: 'Add relay',
    relays_add_title: 'Add a relay',
    relays_add_desc:
      'Name it after where it sits — that is the only way to tell two relays apart in this list — and name the person who answers for it.',
    relays_add_submit: 'Add relay',
    relay_name_label: 'Relay name',
    relays_created: 'Relay added',
    relays_create_failed: 'Failed to add the relay',
    relay_created_title: 'Relay {{name}} added',
    relay_created_desc:
      "Paste this into the relay host's config and restart it. The key is shown only once, and you can revoke it here at any time.",
    relay_created_hint:
      'It appears in this list as offline until its first heartbeat — then it turns online within 30s.',
    relays_id_label: 'Relay ID',
    relay_status_config_label: 'status block in bsod-relay.json',
    relay_copy_config: 'Copy config',
    relays_key_label: 'Relay key',
    relays_copy_key: 'Copy key',
    relays_active_only: 'Hide revoked',
    relays_revoke: 'Revoke key',
    relays_revoke_confirm: 'Revoke the key for {{name}}?',
    relays_revoke_desc:
      'The relay keeps proxying, but its next heartbeat is refused and it disappears from the console. Add the same name again to issue a new key.',
    relays_edit: 'Edit',
    relay_edit_title: 'Edit {{name}}',
    relay_edit_desc:
      "Only the console's own facts change — the label, and who answers for it. The key stays valid and the counters are kept. To issue a new key, add a relay with this name instead.",
    relay_edit_submit: 'Save',
    relays_updated: 'Relay updated',
    relays_update_failed: 'Failed to update the relay',
    relays_name_conflict: 'Another relay already uses that name',
    relay_owner_label: 'Owner',
    relay_owner_none: 'Unassigned',
    relay_owner_pick: 'Pick an owner',
    relay_owner_search: 'Search by name or email',
    relay_owner_empty: 'No users match',
    relays_owner_required: 'Pick an owner',
    relay_rt_revoked: 'Revoked',
    relays_empty: 'No relays yet',
    relays_empty_hint:
      'A relay appears here as soon as it heartbeats. Add it with the button above, then paste the generated `status` block into the relay config file.',
    relay_rt_online: 'Online',
    relay_rt_offline: 'Offline',
    relay_egress_ok: 'Reachable',
    relay_egress_degraded: 'Unreachable',
    relay_egress_unknown: 'Unknown',
    relay_detail_client_acl: 'Client ACL',
    relay_detail_ports: 'Egress ports',
    relay_detail_beacon: 'Discovery beacon',
    relay_detail_beacon_on: 'on, UDP {{port}}',
    relay_detail_beacon_off: 'off',
    relay_detail_connections: 'Connections',
    relay_detail_connections_value: '{{active}} active · {{total}} total',
    relay_detail_rejects: 'Rejected (ACL / allow list)',
    relay_detail_started: 'Started',
    relay_detail_offline_alert: 'Last offline alert',
    relay_detail_offline_alert_none: 'not sent',
    relay_detail_last_error: 'Last error',
    relay_detail_egress_checked: 'Egress self-test · {{when}}',
    relay_detail_egress_pending: 'Egress self-test: not run yet',
    relay_detail_egress_failed: '{{count}} failing',
    relay_detail_no_targets: 'No egress result reported yet.',
    // PI Credentials
    pi_creds_title: 'PI Credentials',
    pi_creds_subtitle: 'Provider credentials used by analysis workers.',
    pi_creds_new: 'New credential',
    pi_creds_col_label: 'Label',
    pi_creds_col_protocol: 'Protocol',
    pi_creds_col_model: 'Model',
    pi_creds_col_endpoint: 'Endpoint',
    pi_creds_col_active: 'Active',
    pi_creds_col_rotated: 'Rotated',
    pi_creds_active_yes: 'Yes',
    pi_creds_active_no: 'No',
    pi_creds_empty: 'No credentials',
    pi_creds_edit: 'Edit',
    pi_creds_delete: 'Delete',
    pi_creds_activate: 'Set active',
    pi_creds_activate_confirm: 'Make this the active credential?',
    pi_creds_activate_desc: 'The currently active credential will be deactivated.',
    pi_creds_delete_confirm: 'Delete this credential?',
    pi_creds_delete_active_desc:
      'This is the active credential — workers will have no credential until another is activated.',
    // Mail notifications
    mail_title: 'Mail notifications',
    mail_subtitle: 'EWS mailbox used to email a project’s leads when an analysis finishes.',
    mail_ews_title: 'Exchange (EWS)',
    mail_ews_desc:
      'On-premises Exchange with a domain account normally uses NTLM. The password is encrypted at rest and never shown again.',
    mail_enabled: 'Send notifications',
    mail_enabled_desc: 'When off, analyses finish silently — the configuration below is kept.',
    mail_host: 'EWS host',
    mail_host_desc: 'FQDN of the Exchange server. Ignored when a full URL is given below.',
    mail_url: 'EWS URL (optional)',
    mail_url_desc: 'Only needed for a non-standard port or path.',
    mail_mailbox: 'Mailbox (From)',
    mail_username: 'Login name',
    mail_domain: 'Domain (optional)',
    mail_domain_desc: 'Leave blank to derive it from the login name (DOMAIN\\user or user@domain).',
    mail_password: 'Password',
    mail_password_keep: '•••••••• (leave blank to keep)',
    mail_auth: 'Authentication',
    mail_verify_tls: 'Verify TLS certificate',
    mail_verify_tls_desc:
      'Turn off only if the internal certificate is not trusted by the backend.',
    mail_language: 'Email language',
    mail_updated_at: 'Last updated {{when}}',
    mail_never_saved: 'Not configured yet',
    mail_test_title: 'Send a test email',
    mail_test_desc:
      'Uses the settings above as they are stored, so save first. Works while notifications are still off.',
    mail_test_to: 'Recipient',
    mail_test_send: 'Send test',
    mail_test_sent: 'Test email sent — check the inbox.',
    mail_saved: 'Mail settings saved',
    mail_err_host: 'Enter the Exchange host or a full EWS URL.',
    mail_err_mailbox: 'Enter a valid email address.',
    mail_err_username: 'Enter the login name.',
    mail_err_password: 'A password is required for the first setup.',
    // Audit Logs
    audit_logs_title: 'Audit Logs',
    audit_logs_subtitle: 'Append-only record of administrative and data-modifying actions.',
    audit_logs_filter_entity: 'Filter by entity type (e.g. user)',
    audit_logs_filter_action: 'Filter by action (e.g. user.disable)',
    audit_logs_col_when: 'When',
    audit_logs_col_actor: 'Actor',
    audit_logs_col_action: 'Action',
    audit_logs_col_entity: 'Entity',
    audit_logs_col_entity_id: 'Entity id',
    audit_logs_col_project_id: 'Project id',
    audit_logs_col_project: 'Project',
    audit_logs_empty: 'No audit logs',
    audit_logs_actor_worker: 'Worker',
    audit_logs_actor_system: 'System',
    audit_logs_entity_user: 'User',
    audit_logs_entity_project: 'Project',
    audit_logs_entity_case: 'Case',
    audit_logs_entity_event: 'Event',
    audit_logs_entity_company: 'Company',
    audit_logs_entity_device: 'Device',
    audit_logs_entity_pending_user: 'Pending user',
    audit_logs_entity_task: 'Task',
    audit_logs_entity_upload_pin: 'Upload PIN',
    audit_logs_entity_pi_credential: 'PI credential',
    audit_logs_action_case_auto_create: 'Created case automatically',
    audit_logs_action_case_reopen_by_worker: 'Reopened case automatically',
    audit_logs_action_pending_user_approve: 'Approved pending user',
    audit_logs_action_pending_user_reject: 'Rejected pending user',
    audit_logs_action_task_cancel: 'Cancelled task',
    audit_logs_action_task_restore: 'Restored task',
    audit_logs_action_upload_pin_delete: 'Deleted upload PIN',
    audit_logs_action_upload_pin_revoke: 'Revoked upload PIN',
    audit_logs_action_device_catalog_assign: 'Assigned device catalog entry',
    audit_logs_action_device_catalog_reassign: 'Changed device catalog entry',
    audit_logs_action_device_catalog_unassign: 'Removed device catalog entry',
    // Queue Health
    queue_health_title: 'Queue Health',
    queue_health_subtitle: 'Analysis job queue state and dead-letter inspection.',
    queue_health_pending: 'Pending',
    queue_health_claimed: 'Claimed',
    queue_health_done: 'Done',
    queue_health_failed: 'Failed',
    queue_health_dead: 'Dead',
    queue_health_cancelled: 'Cancelled',
    queue_health_recent: 'Recent tasks',
    queue_health_requeue: 'Requeue',
    queue_health_requeue_confirm: 'Requeue this task?',
    queue_health_cancel: 'Cancel task',
    queue_health_cancel_confirm:
      'Cancel this task? The row is kept for reference and can be restored.',
    queue_health_cancel_pending_note:
      'This event is left with no queued task, so it will be marked failed.',
    queue_health_restore: 'Restore to pending',
    queue_health_restore_confirm: 'Restore this task to pending and let a worker claim it?',
    queue_health_col_event: 'Event',
    queue_health_col_status: 'Status',
    queue_health_col_attempts: 'Attempts',
    queue_health_col_last_error: 'Last error',
    queue_health_col_updated: 'Updated',
    queue_health_empty: 'No tasks yet',
    // OIDC Providers
    oidc_title: 'OIDC Providers',
    // Releases (client binaries)
    releases_title: 'Releases',
    releases_subtitle: 'Publish client binaries. Downloads require a signed-in account.',
    releases_empty: 'No releases yet',
    release_new: 'Upload new version',
    release_status: 'Status',
    release_kind: 'Kind',
    release_version: 'Version',
    release_version_required: 'Version is required',
    release_version_invalid: 'Use semver, e.g. 1.2.0',
    release_identity_locked:
      'Fixed once the draft is created — it is part of the artifact name. Delete the draft to change it.',
    release_channel: 'Channel',
    release_size: 'Size',
    release_sha256: 'SHA-256',
    release_created: 'Created',
    release_published_at: 'Released',
    release_actions: 'Actions',
    release_edit: 'Edit',
    release_publish: 'Publish',
    release_yank: 'Yank',
    release_restore: 'Restore',
    release_confirm_delete: 'Delete this draft?',
    release_notes: 'Release notes',
    release_notes_placeholder: 'What changed in this version',
    release_notes_hint:
      'Internal changelog; the download page does not show it — that is the “Usage” text below.',
    release_usage: 'Usage',
    release_usage_placeholder:
      'How to use this build; Markdown, with commands or config in triple backticks.',
    release_usage_hint:
      'Shown on the download page under “Usage”. Leave empty to keep the built-in text.',
    release_usage_builtin: 'What a reader sees with this box empty',
    release_usage_not_saved:
      'The server did not store the usage text (the backend may be an older build that does not know this field). Restart or redeploy it and try again.',
    release_step_meta: 'Details',
    release_step_upload: 'Upload',
    release_step_verify: 'Verify',
    release_file: 'Artifact',
    release_file_hint: 'Uploaded straight to object storage — the app server never sees the bytes.',
    release_choose_file: 'Choose or drop the artifact to publish (.exe / .zip / .7z)',
    release_file_mismatch: 'The file name must match the kind and version; expected',
    release_upload_failed: 'Upload failed',
    release_publish_now: 'Verify and publish',
    release_save_draft: 'Save as draft',
    release_save: 'Save',
    release_cancel: 'Cancel',
    release_updated: 'Release updated',
    release_action_failed: 'Action failed',
    release_deleted: 'Draft deleted',
    release_published: 'Release published',
    release_saved_draft: 'Saved as draft',
    release_yanked: 'Release yanked',
    oidc_subtitle: 'Manage OpenID Connect identity providers for SSO login.',
    oidc_add: 'Add provider',
    oidc_col_slug: 'Slug',
    oidc_col_display_name: 'Display name',
    oidc_col_issuer: 'Issuer',
    oidc_col_client_id: 'Client ID',
    oidc_col_enabled: 'Enabled',
    oidc_status_enabled: 'Enabled',
    oidc_status_disabled: 'Disabled',
    oidc_empty: 'No OIDC providers',
    // Common
    never: 'never',
    token_done: 'Done',
  },
  event_detail: {
    breadcrumb_events: 'Events',
    reanalyze: 'Reanalyze',
    download: 'Download',
    share: 'Share',
    ai_verdict: 'AI verdict',
    confidence: 'Confidence: {{level}}',
    faulting_module: 'Faulting module',
    stack_frames: 'Stack frames',
    modules: 'Modules',
    uptime_at_crash: 'Uptime at crash',
    crash_site: 'Crash site — top of stack',
    tab_summary: 'Summary',
    tab_report: 'Report',
    tab_stack: 'Stack',
    tab_modules: 'Modules',
    tab_system: 'System',
    tab_files: 'Files',
    tab_runs: 'Runs',
    tab_more: 'More',
    meta_description: 'Description',
    meta_description_hint:
      'Human-readable context provided at upload time. Editable by PAs and leads.',
    meta_tags_hint: 'Free-form labels for triage. Searchable from the events list via tag:value.',
    danger_zone: 'Delete',
    danger_hint: 'Permanently remove this event from lists (soft-delete, kept for audit).',
    delete_event: 'Delete this event',
    delete_confirm_title: 'Delete this event?',
    delete_confirm_desc: 'This cannot be undone.',
    record: 'Record',
    bugcheck: 'Bugcheck',
    suspect: 'Suspect',
    machine: 'Machine',
    source: 'Source',
    tracking: 'Tracking',
    code: 'Code',
    name: 'Name',
    suspect_module: 'Module',
    suspect_bucket: 'Bucket',
    suspect_bucket_id: 'Bucket id',
    computer: 'Computer',
    system: 'System',
    os: 'OS',
    cpu: 'CPU',
    memory: 'Memory',
    size: 'Size',
    uploaded: 'Uploaded',
    sha: 'SHA-256',
    run: 'Run',
    device: 'Device',
    case: 'Case',
    tags: 'Tags',
    third_party: '3rd party',
    third_party_hint: 'Modules not signed by Microsoft',
    no_summary: 'No summary available',
    no_analysis: 'Analysis pending',
    viewing_older_run: 'Viewing an older run (#{{n}}). Latest is run #{{latest}}.',
    switch_to_latest: 'Switch to latest →',
    untitled_analysis: 'Untitled analysis',
    deleted_toast: 'Event deleted',
    live_unavailable: 'Live updates unavailable — falling back to polling every 5s.',
    brief: 'Brief',
    report_pending_hint: 'The worker is parsing the dump. This page updates automatically.',
    report_empty: 'No analysis result',
    report_empty_hint: 'Try Reanalyze.',
    files_upload: 'Upload file',
    files_stage_hashing: 'Hashing…',
    files_stage_uploading: 'Uploading…',
    files_stage_finalizing: 'Finalizing…',
    files_upload_progress: 'Uploading {{name}}',
    files_uploaded: 'Uploaded {{name}}',
    files_upload_failed: 'Upload failed',
    files_empty: 'No files',
    files_col_file: 'File',
    files_col_size: 'Size',
    files_col_uploaded: 'Uploaded',
    files_kind_primary: 'Primary dump',
    files_kind_attachment: 'Attachments',
    files_download_aria: 'Download {{name}}',
    runs_run_n: 'Run #{{n}}',
    runs_empty: 'No runs',
    runs_running: 'running',
    runs_latest: 'latest',
    runs_viewing: 'viewing',
    runs_col_run: 'Run',
    runs_col_status: 'Status',
    runs_col_duration: 'Duration',
    runs_col_finished: 'Finished',
    verdict_extractor: 'Extractor',
    share_create_link: 'Create link',
    share_ttl_1d: '1 day',
    share_ttl_7d: '7 days',
    share_copy: 'Copy',
    share_revoke: 'Revoke',
    share_revoke_confirm: 'Revoke this link?',
    share_expires: 'expires {{date}}',
    share_copied: 'Link copied',
    share_group_link: 'Share link',
    share_ttl_label: 'Link lifetime',
    share_group_report: 'Report',
    share_format_label: 'Report format and language',
    share_download: 'Download',
    share_generate_hint: 'Download the report in the chosen format and language',
    share_no_report: 'No analysis report available for this event yet',
    share_load_failed: 'Could not load the report',
    share_export_failed: 'Report generation failed',
    share_export_done: 'Report generated',
    share_export_pdf: 'Generating PDF…',
    share_export_page: 'Rendering page {{current}} / {{total}}…',
    share_export_collecting: 'Collecting document…',
    desc_empty: 'No description',
    desc_edit: 'Edit',
    desc_add: 'Add description',
    tags_empty: 'No tags',
    tags_remove: 'Remove {{tag}}',
    tags_placeholder: 'tag name',
    tags_new: 'New tag',
    no_analysis_yet: 'No analysis yet',
    no_analysis_hint: 'Available once the worker finishes parsing the dump.',
    stack_empty: 'No stack frames',
    stack_empty_hint: 'The dump parsed but captured no frames. See the Report tab.',
    stack_count: '{{count}} frames · #{{top}} is the crash site',
    modules_empty: 'No modules',
    modules_empty_hint: 'The dump lm v output produced no modules.',
    modules_count: '{{count}} modules',
    modules_microsoft: '{{count}} Microsoft',
    modules_third_party: '{{count}} 3rd party',
    modules_col_name: 'Name',
    modules_col_version: 'Version',
    modules_col_company: 'Company',
    modules_col_base: 'Base',
    modules_col_size: 'Size',
    module_suspect: 'suspect',
    sys_identity: 'Identity',
    sys_manufacturer: 'Manufacturer',
    sys_product: 'Product',
    sys_version: 'Version',
    sys_family: 'Family',
    sys_sku: 'SKU',
    sys_serial: 'Serial',
    sys_smbios_uuid: 'SMBIOS UUID',
    sys_last_user: 'Last user',
    sys_os: 'Operating system',
    sys_os_name: 'Name',
    sys_os_version: 'Version / build',
    sys_os_edition: 'Edition',
    sys_os_arch: 'Architecture',
    sys_os_uptime: 'Uptime at crash',
    sys_cpu: 'Processor',
    sys_cpu_vendor: 'Vendor / model',
    sys_cpu_family: 'Family',
    sys_cpu_model: 'Model',
    sys_cpu_stepping: 'Stepping',
    sys_cpu_cores: 'Cores',
    sys_cpu_cores_value: '{{physical}} physical · {{logical}} logical',
    sys_cpu_speed: 'Base speed',
    sys_cpu_microcode: 'Microcode',
    sys_memory: 'Memory',
    sys_mem_total: 'Total physical',
    sys_mem_available: 'Available at crash',
    sys_bios: 'BIOS / Firmware',
    sys_bios_vendor: 'Vendor',
    sys_bios_version: 'Version',
    sys_bios_date: 'Release date',
    sys_bios_revision: 'BIOS revision',
    sys_fw_revision: 'Firmware revision',
    sys_baseboard: 'Baseboard',
    sys_adapters: 'Network adapters ({{count}})',
    sys_adapters_empty: 'No adapters captured.',
    sys_adapters_col_name: 'Name',
    sys_adapters_col_mac: 'MAC',
    sys_adapters_col_media: 'Media',
    sys_adapters_col_driver: 'Driver',
    sys_adapters_col_pnp: 'Pnp ID',
    sys_adapter_removable: 'removable',
    sys_adapter_onboard: 'onboard',
    uptime_na: '—',
  },
} as const;

type Widen<T> = {
  [K in keyof T]: T[K] extends string ? string : Widen<T[K]>;
};

export type Messages = Widen<typeof en>;
